/**
 * ImpersonationBanner — persistent "You are viewing as …" bar shown whenever
 * the App Super Admin is impersonating another account. One-click Exit
 * restores the super-admin's own session. Polls /superadmin/impersonate/status.
 *
 * Mounted globally (inside Router + AuthProvider). Renders nothing when not
 * impersonating.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, LogOut, Loader2 } from "lucide-react";

interface Status {
  impersonating: boolean;
  actor?: { user_id: string; email: string; name: string } | null;
  viewing_as?: { user_id: string; email: string; name: string } | null;
}

export const ImpersonationBanner: React.FC = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [st, setSt] = useState<Status>({ impersonating: false });
  const [exiting, setExiting] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get<Status>("/superadmin/impersonate/status");
      setSt(data);
    } catch {
      setSt({ impersonating: false });
    }
  }, []);

  useEffect(() => { check(); }, [check, user?.user_id]);

  // Push page content down while the fixed banner is visible.
  useEffect(() => {
    document.body.style.paddingTop = st.impersonating ? "40px" : "";
    return () => { document.body.style.paddingTop = ""; };
  }, [st.impersonating]);

  const exit = async () => {
    setExiting(true);
    try {
      const { data } = await api.post("/superadmin/impersonate/stop");
      if (data?.token) setToken(data.token);
      await refresh();
      setSt({ impersonating: false });
      toast.success("Exited impersonation — back to your Super Admin account");
      navigate("/superadmin");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Could not exit impersonation");
    } finally { setExiting(false); }
  };

  if (!st.impersonating) return null;
  const who = st.viewing_as?.name || st.viewing_as?.email || "another account";

  return (
    <div
      data-testid="impersonation-banner"
      className="fixed top-0 inset-x-0 z-[9999] bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg"
    >
      <div className="max-w-[1760px] mx-auto px-4 py-2 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="font-semibold truncate">
            You are viewing as <span className="underline underline-offset-2">{who}</span>
            {st.viewing_as?.email ? <span className="hidden sm:inline opacity-90"> · {st.viewing_as.email}</span> : null}
          </span>
        </div>
        <button
          onClick={exit}
          disabled={exiting}
          data-testid="impersonation-exit"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-white/95 text-orange-700 hover:bg-white px-3 py-1 text-xs font-bold disabled:opacity-60"
        >
          {exiting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
          Exit
        </button>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
